import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminSubpage from '../components/AdminSubpage';
import SearchableCountrySelect from '../components/SearchableCountrySelect';
import { adminCountriesAPI, adminShippingRatesAPI } from '../services/api';

const emptyForm = {
  country_id: '',
  label: '',
  amount: '',
  currency: 'AED',
  is_active: true,
};

const ShippingRateFormPage = () => {
  const { rateId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(rateId);
  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const countriesRes = await adminCountriesAPI.getAll();
        if (!cancelled) setCountries(countriesRes.data?.countries || []);
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isEdit) {
      setFormData(emptyForm);
      setLoading(false);
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const ratesRes = await adminShippingRatesAPI.getAll();
        const rates = ratesRes.data?.shipping_rates || [];
        const rate = rates.find((r) => String(r.id) === String(rateId));
        if (!cancelled) {
          if (rate) {
            setFormData({
              country_id: rate.country_id || '',
              label: rate.label || '',
              amount: String(rate.amount ?? ''),
              currency: rate.currency || 'AED',
              is_active: Boolean(rate.is_active),
            });
          } else {
            toast.error('Shipping rate not found');
          }
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rateId, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        country_id: formData.country_id,
        label: formData.label || null,
        amount: parseFloat(formData.amount),
        currency: formData.currency.toUpperCase(),
        is_active: formData.is_active,
      };
      if (isEdit) {
        await adminShippingRatesAPI.update(rateId, payload);
        toast.success('Shipping rate updated');
      } else {
        await adminShippingRatesAPI.create(payload);
        toast.success('Shipping rate created');
      }
      navigate('/shipping-rates');
    } catch (error) {
      const msg =
        error.response?.data?.errors && typeof error.response.data.errors === 'object'
          ? Object.values(error.response.data.errors).flat().join(' ')
          : error.response?.data?.message || error.message;
      toast.error('Error saving shipping rate: ' + msg);
    }
  };

  const title = isEdit ? 'Edit shipping rate' : 'Add shipping rate';

  return (
    <AdminSubpage title={title} backTo="/shipping-rates">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-page-panel modal-content">
          <form onSubmit={submit}>
            <div className="form-group">
              <label>Country</label>
              <SearchableCountrySelect
                countries={countries}
                value={formData.country_id}
                onChange={(id) => setFormData({ ...formData, country_id: id })}
                disabled={isEdit}
                placeholder={isEdit ? 'Country locked' : 'Search country…'}
              />
            </div>
            <div className="form-group">
              <label>Label</label>
              <input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                maxLength={120}
              />
            </div>
            <div className="form-group">
              <label>Amount *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Currency *</label>
              <input
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                maxLength={3}
                required
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />{' '}
                Active
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {isEdit ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/shipping-rates')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default ShippingRateFormPage;
